import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import {
  unwrapRootKeyFromPasskey,
  wrapRootKeyForPasskey,
  type RecoveryWrappedKey,
} from '@trove/crypto';
import { deleteJson, postJson } from './api';

const PASSKEY_UNLOCK_STORAGE_KEY = 'trove-passkey-unlocks-v1';
const encoder = new TextEncoder();

export interface PasskeyVaultKey {
  repositoryId: string;
  collectionKey: number[];
}

type PrfExtensionResults = {
  prf?: { enabled?: boolean; results?: { first?: ArrayBuffer } };
};

const passkeyUnlockSalt = async () =>
  crypto.subtle.digest('SHA-256', encoder.encode('trove-passkey-unlock-v1'));

const withPrf = async <T extends { extensions?: object }>(options: T): Promise<T> =>
  ({
    ...options,
    extensions: {
      ...options.extensions,
      prf: { eval: { first: await passkeyUnlockSalt() } },
    },
  }) as T;

const prfSecretFrom = (response: { clientExtensionResults?: object }): Uint8Array | null => {
  const first = (response.clientExtensionResults as PrfExtensionResults | undefined)?.prf?.results
    ?.first;
  return first ? new Uint8Array(first) : null;
};

export const loadPendingPasskeyUnlocks = (): PasskeyVaultKey[] => {
  try {
    const parsed = JSON.parse(
      sessionStorage.getItem(PASSKEY_UNLOCK_STORAGE_KEY) ?? '[]',
    ) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((value): value is PasskeyVaultKey =>
      Boolean(
        value &&
        typeof value === 'object' &&
        'repositoryId' in value &&
        typeof value.repositoryId === 'string' &&
        'collectionKey' in value &&
        Array.isArray(value.collectionKey),
      ),
    );
  } catch {
    return [];
  }
};

export const clearPendingPasskeyUnlocks = () =>
  sessionStorage.removeItem(PASSKEY_UNLOCK_STORAGE_KEY);

export const passkeysSupported = () => typeof window !== 'undefined' && browserSupportsWebAuthn();

const authenticateAndStageVaultKeys = async (optionsPath: string) => {
  const { options } = await postJson<{ options: PublicKeyCredentialRequestOptionsJSON }>(
    optionsPath,
    {},
  );
  const response = await startAuthentication({
    optionsJSON: await withPrf(options),
  });
  const prfSecret = prfSecretFrom(response);
  const result = await postJson<{
    ok: true;
    vaultWraps: Array<{ repositoryId: string; wrappedKey: RecoveryWrappedKey }>;
  }>('/v1/auth/passkey/verify', { response });
  if (!prfSecret || result.vaultWraps.length === 0) return { unlockedVaults: 0 };
  const unlocked = await Promise.all(
    result.vaultWraps.map(async ({ repositoryId, wrappedKey }) => ({
      repositoryId,
      collectionKey: [...(await unwrapRootKeyFromPasskey(wrappedKey, prfSecret, repositoryId))],
    })),
  );
  sessionStorage.setItem(PASSKEY_UNLOCK_STORAGE_KEY, JSON.stringify(unlocked));
  return { unlockedVaults: unlocked.length };
};

export const signInWithPasskey = () => authenticateAndStageVaultKeys('/v1/auth/passkey/options');

export const unlockVaultWithPasskey = () =>
  authenticateAndStageVaultKeys('/v1/passkeys/unlock/options');

export async function registerPasskey(
  label?: string,
  icon?: 'fingerprint' | 'laptop' | 'smartphone' | 'key' | 'shield' | 'watch',
  vault?: { repositoryId: string; collectionKey: Uint8Array },
) {
  const { options } = await postJson<{ options: PublicKeyCredentialCreationOptionsJSON }>(
    '/v1/passkeys/register/options',
    {},
  );
  const response: RegistrationResponseJSON = await startRegistration({
    optionsJSON: await withPrf(options),
  });
  const prfSecret = prfSecretFrom(response);
  const vaultWraps =
    vault && prfSecret
      ? [
          {
            repositoryId: vault.repositoryId,
            wrappedKey: await wrapRootKeyForPasskey(
              vault.collectionKey,
              prfSecret,
              vault.repositoryId,
            ),
          },
        ]
      : undefined;
  return postJson<{ ok: true; id: string; unlockEnabled: boolean }>(
    '/v1/passkeys/register/verify',
    { response, label, icon, vaultWraps },
  );
}

export async function enablePasskeyUnlock(
  passkeyId: string,
  vault: { repositoryId: string; collectionKey: Uint8Array },
) {
  const { options } = await postJson<{ options: PublicKeyCredentialRequestOptionsJSON }>(
    `/v1/passkeys/${passkeyId}/unlock/options`,
    {},
  );
  const response = await startAuthentication({ optionsJSON: await withPrf(options) });
  const prfSecret = prfSecretFrom(response);
  if (!prfSecret) {
    throw new Error('This passkey provider does not support encrypted vault unlock.');
  }
  const wrappedKey = await wrapRootKeyForPasskey(
    vault.collectionKey,
    prfSecret,
    vault.repositoryId,
  );
  await postJson(`/v1/passkeys/${passkeyId}/unlock`, {
    response,
    repositoryId: vault.repositoryId,
    wrappedKey,
  });
}

export async function revokePasskey(id: string) {
  await deleteJson(`/v1/passkeys/${id}`);
}
